import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, AlertCircle, ExternalLink } from "lucide-react";
import { PageHeader } from "@/components/shared/PageHeader";
import { useLinearIssues, useLinearProjects, type LinearIssue } from "@/hooks/useLinear";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { LINEAR_PRIORITY_TONE } from "@/lib/statusTone";

const PRIORITY_LABELS: Record<number, string> = {
  0: "Sem prioridade",
  1: "Urgente",
  2: "Alta",
  3: "Média",
  4: "Baixa",
};

function IssuePriorityBadge({ priority }: { priority: number }) {
  return (
    <StatusBadge shape="tag" tone={LINEAR_PRIORITY_TONE[priority] ?? "neutral"}>
      {PRIORITY_LABELS[priority] ?? "—"}
    </StatusBadge>
  );
}

function IssueStateBadge({ state }: { state: LinearIssue["state"] }) {
  return (
    <span className="flex items-center gap-1.5 text-xs">
      <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: state.color }} />
      {state.name}
    </span>
  );
}

export function LinearIssues() {
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [stateFilter, setStateFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");

  const { data: projectsData } = useLinearProjects();
  const projects = projectsData?.projects?.nodes ?? [];

  const { data, isLoading, error } = useLinearIssues(
    projectFilter !== "all" ? projectFilter : null,
  );
  const allIssues = data?.issues?.nodes ?? [];

  const issues = allIssues.filter((i) => {
    if (stateFilter !== "all" && i.state.type !== stateFilter) return false;
    if (priorityFilter !== "all" && String(i.priority) !== priorityFilter) return false;
    return true;
  });

  const stateTypes = [...new Set(allIssues.map((i) => i.state.type))];

  return (
    <div className="space-y-6">
      <PageHeader title="Issues — Linear" description="Todas as issues do seu workspace." />

      <div className="flex flex-wrap gap-3">
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Projeto" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os projetos</SelectItem>
            {projects.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={stateFilter} onValueChange={setStateFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            {stateTypes.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Prioridade" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as prioridades</SelectItem>
            {Object.entries(PRIORITY_LABELS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="ml-auto self-center text-sm text-muted-foreground">
          {issues.length} issue{issues.length !== 1 ? "s" : ""}
        </span>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {error && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="flex items-start gap-3 p-6">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <div>
              <p className="text-sm font-medium">Não foi possível conectar ao Linear</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Configure <code className="rounded bg-muted px-1 font-mono">LINEAR_API_KEY</code>{" "}
                via{" "}
                <code className="rounded bg-muted px-1 font-mono">
                  supabase secrets set LINEAR_API_KEY=lin_api_xxxxx
                </code>
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Título</TableHead>
                  <TableHead>Projeto</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Prioridade</TableHead>
                  <TableHead>Responsável</TableHead>
                  <TableHead>Atualizado</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {issues.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                      Nenhuma issue encontrada.
                    </TableCell>
                  </TableRow>
                )}
                {issues.map((issue) => (
                  <TableRow key={issue.id}>
                    <TableCell className="max-w-[280px] font-medium">
                      <span className="line-clamp-2 text-sm">{issue.title}</span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {issue.project?.name ?? "—"}
                    </TableCell>
                    <TableCell>
                      <IssueStateBadge state={issue.state} />
                    </TableCell>
                    <TableCell>
                      <IssuePriorityBadge priority={issue.priority} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {issue.assignee?.name ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {format(new Date(issue.updatedAt), "dd/MM/yy HH:mm", { locale: ptBR })}
                    </TableCell>
                    <TableCell>
                      <a
                        href={issue.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-muted-foreground transition-colors hover:text-foreground"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
